// از جوابِ `admin_health` تا عقربه‌ها (WHOSUP-264).
//
// عمداً در core و نه در SQL — همان تقسیمِ کاری که `addressReport.ts` و
// `sourceTest.ts` دارند: دیتابیس **می‌شمارد** (مایگریشن 0153)، این‌جا تصمیم
// گرفته می‌شود که چه عددی سبز است و چه عددی قرمز، و «بهتر شده» یعنی چه.
// عوض کردنِ یک آستانه نباید مایگریشن بخواهد، و `npm test` با اعدادِ ساختگی
// می‌آزمایدش — بدونِ دیتابیس، بدونِ مرورگر.
//
// ═══ شکلِ حکم ═══
//
// هر عقربه یک عدد دارد و دو آستانه: `good` (عقربه ته‌ِ سبز، امتیاز ۱۰۰) و
// `bad` (ته‌ِ قرمز، امتیاز ۰). بینشان خطی است. اگر `good < bad` باشد یعنی
// **کمتر بهتر است** (خطا، بی‌مختصات)؛ برعکسش یعنی بیشتر بهتر است (رشدِ
// نشست‌ها). امتیاز به رنگ تبدیل می‌شود (`levelOf`) و میانگینِ امتیازها
// عقربه‌ی هر حوزه و عقربه‌ی کل را می‌سازد.
//
// ⚠ «نامعلوم» حکم نیست و رنگ ندارد: عقربه‌ای که ورودی‌اش نیست (لاگِ فعالیت
// خالی است، اسکیمای کرالر نیست) از میانگین بیرون می‌ماند و خودش می‌گوید
// چرا. صفرِ «چیزی ثبت نشده» را سبز نشان دادن، همان خرابیِ بی‌صدایی است که
// این صفحه برای دیدنش هست.
//
// ═══ «نسبت به قبل» ═══
//
// هر عکسِ روزانه در `history` است؛ مقایسه با **آخرین روزِ غیر از امروز**
// انجام می‌شود، نه با عکسِ قبلیِ همان روز — دو بار باز کردنِ صفحه در یک
// ساعت «بهتر شد» نمی‌سازد. جهتِ «بهتر» از همان آستانه‌ها می‌آید.

/** واقعیت‌های چهار حوزه — عیناً کلیدهای `health_facts()` در مایگریشن 0153. */
export interface HealthContentFacts {
  live_events: number;
  no_photo: number;
  no_geo: number;
  no_address: number;
  /**
   * از برچسب‌های خودِ کرالر در `events.metadata` (مایگریشن 0189، WHOSUP-322):
   * ساعتِ نامعلوم = `time_precision = 'date'` (تمام‌روز حساب نمی‌شود)، قیمتِ
   * نامعلوم = `price_status = 'unknown'`. عکس‌های پیش از 0189 ندارندش.
   */
  no_time?: number;
  no_price?: number;
  stale: number;
  created_24h: number;
  created_7d: number;
  pending_review: number;
  /**
   * گزارش‌های محتوای کاربران (مایگریشن 0160): بازها، و بازهایی که بیش از
   * ۲۴ ساعت مانده‌اند — قولِ بندِ ۱۰ شرایط. عکس‌های پیش از 0160 ندارندش.
   */
  open_reports?: number;
  overdue_reports?: number;
  invalid_images: number;
  broken_image_reports_7d: number;
  broken_image_urls_7d: number;
  /** آخرین `events.created_at` در ۷ روزِ گذشته (مایگریشن 0160)؛ کهنه‌تر یا پیش از 0160 → نمی‌آید. */
  last_created_at?: string | null;
  /**
   * بارِ شهر (مایگریشن 0157): بیشترینِ «کاربرانِ فعالِ ۶۰ روز ÷ رویدادهای
   * تازه‌ی ۳ روز» روی شهرها، و همان شهر. عکس‌های پیش از 0157 ندارندش.
   */
  city_load_max?: number | null;
  city_load_city?: string | null;
  city_load_users_60d?: number | null;
  city_load_new_3d?: number | null;
  /**
   * همان نسبت برای همه‌ی شهرها روی هم، و هر شهر جدا (بازخوردِ WHOSUP-303،
   * مایگریشن 0161). عکس‌های پیش از آن ندارندش.
   */
  city_load_users_total?: number | null;
  city_load_new_total?: number | null;
  city_loads?: { city: string; country_code: string; users_60d: number; new_3d: number }[];
  /**
   * همان شمارش‌های بی‌عکس/بی‌مختصات/بی‌آدرس به تفکیکِ شهر (مایگریشن 0161)،
   * برای همه‌ی شهرهای دارای رویدادِ زنده. عکس‌های پیش از 0161 ندارندش.
   */
  by_city?: HealthCityFacts[];
}

export interface HealthCityFacts {
  city: string;
  country_code: string;
  live: number;
  no_photo: number;
  no_geo: number;
  no_address: number;
  /** این سه بعدتر به همان مایگریشن اضافه شدند؛ عکسی که ندارد → «نامعلوم». */
  stale?: number;
  pending_review?: number;
  broken_image_urls_7d?: number;
  /** از مایگریشن 0189. */
  no_time?: number;
  no_price?: number;
}

export interface HealthAppFacts {
  errors_24h: number;
  errors_7d: number;
  error_users_7d: number;
  last_error_at: string | null;
  sessions_24h: number;
  sessions_7d: number;
  sessions_prev_7d: number;
  people_7d: number;
  web_sessions_7d: number;
  mobile_sessions_7d: number;
  last_web_at: string | null;
  last_mobile_at: string | null;
}

export interface HealthDbFacts {
  size_bytes: number;
  connections: number;
  max_connections: number;
  devlog_rows_7d: number;
  queries_7d: number;
  failed_queries_7d: number;
  /** از ۰۱۶۳ (WHOSUP-323) فقط درخواست‌های غیرادمینی؛ پیش از آن همه با هم. */
  slow_queries_7d: number;
  query_p95_ms_7d: number | null;
  /** درخواست‌هایی که نامشان با `rpc.admin_` شروع می‌شود — فقط از ۰۱۶۳. */
  admin_queries_7d?: number;
  admin_slow_queries_7d?: number;
  admin_query_p95_ms_7d?: number | null;
  app_errors_7d: number;
  /** فقط وقتی دفترِ `schema_migrations` هست. */
  last_migration?: string;
  last_migration_at?: string;
  /** فقط وقتی `cron.job_run_details` هست. */
  cron_runs_24h?: number;
  cron_failed_24h?: number;
}

export interface HealthCrawlerFacts {
  /** اسکیمای `crawler` روی این دیتابیس هست؟ بی آن، بقیه‌ی کلیدها نمی‌آیند. */
  present: boolean;
  jobs_queued: number;
  jobs_stale: number;
  jobs_failed_7d: number;
  last_run_started_at?: string | null;
  last_run_finished_at?: string | null;
  last_run_status?: string | null;
  last_run_full?: boolean | null;
  hours_since_run?: number | null;
  last_any_run_at?: string | null;
  stage_runs?: number;
  stage_failures?: number;
  fetch_requests_run?: number;
  fetch_failed_run?: number;
  published_run?: number;
}

export interface HealthFacts {
  content: HealthContentFacts;
  app: HealthAppFacts;
  db: HealthDbFacts;
  crawler: HealthCrawlerFacts;
}

export interface HealthSnapshot {
  /** `YYYY-MM-DD` به وقتِ سرور. */
  day: string;
  taken_at: string;
  facts: HealthFacts;
}

/** جوابِ `admin_health()`. */
export interface HealthData {
  as_of: string;
  /** کِی `now` حساب شده — تا یک ساعت از عکسِ امروز خوانده می‌شود (WHOSUP-324). */
  taken_at?: string;
  now: HealthFacts;
  /** از قدیم به جدید، شاملِ امروز. */
  history: HealthSnapshot[];
}

export type Level = 'ok' | 'warn' | 'bad' | 'unknown';
export type GroupKey = 'content' | 'app' | 'db' | 'crawler';
export type Unit = 'pct' | 'count' | 'ratio' | 'hours' | 'ms' | 'bytes';

export const GROUP_LABEL: Record<GroupKey, string> = {
  content: 'محتوا',
  app: 'سایت و اپ',
  db: 'دیتابیس',
  crawler: 'کرالر',
};

export const GROUP_HELP: Record<GroupKey, string> = {
  content: 'رویدادهایی که کاربر می‌بیند: عکس، مختصات، آدرس، ساعت، قیمت، تازگی. خرابیِ این‌ها از سمتِ کرالر دیده نمی‌شود — این‌جا از سمتِ کاربر شمرده می‌شود.',
  app: 'خودِ سایت و اپ: خطاهایی که دستگاه‌ها فرستاده‌اند (client_errors)، نشست‌ها، و آخرین ضربانِ هر سطح (app_events).',
  db: 'دیتابیس: اتصال‌ها و حجم از خودِ پستگرس؛ کوئری‌ها از لاگِ فعالیت (dev_log) که فقط وقتی پر می‌شود حسابِ صاحبِ ابزار وارد باشد؛ کرون‌ها از pg_cron.',
  crawler: 'خطِ لوله‌ی شبانه: تازگیِ آخرین اجرای کامل، فازهای شکست‌خورده، خطای گرفتنِ صفحه‌ها، و صفِ کارهای ادمین.',
};

/**
 * میان‌برِ یک سربرگِ ادمین — `?tab=…` همان پارامتری است که `useUrlState`
 * در `apps/admin` می‌خواند؛ `surface` هم پارامترِ سربرگِ «فعالیتِ کاربران».
 */
const tab = (label: string, id: string): GaugeLink =>
  ({ label: `سربرگِ «${label}»`, href: `?tab=${id}` });
const TAB = {
  crawler: tab('خطِ لوله‌ی کرالر', 'crawler'),
  sources: tab('منابع', 'sources'),
  review: tab('صفِ بازبینی', 'review'),
  reports: tab('گزارش‌ها', 'reports'),
  photos: tab('عکس‌ها', 'photos'),
  addresses: tab('آدرس‌ها', 'addresses'),
  activity: tab('فعالیتِ کاربران', 'activity'),
  activityWeb: { label: 'سربرگِ «فعالیتِ کاربران» — فقط سایت', href: '?tab=activity&surface=web' },
  activityMobile: { label: 'سربرگِ «فعالیتِ کاربران» — فقط اپ', href: '?tab=activity&surface=mobile' },
} as const;

/** راهنمای عقربه‌ی بزرگِ هر حوزه. */
export const GROUP_GUIDE: Record<GroupKey, GaugeGuide> = {
  content: {
    meaning: 'رویدادهایی که کاربر در اپ و سایت می‌بیند چقدر کامل و درست‌اند: عکس دارند، روی نقشه می‌نشینند، آدرس دارند، تمام‌شده نیستند.',
    example: 'کاربر فهرستِ «کشف» را ورق می‌زند. اگر هر سه کارت یکی بی‌عکس است، یکی آدرس ندارد و یکی مالِ پریروز است، دیگر به فهرست اعتماد نمی‌کند — حتی اگر هیچ چیزی «خراب» نشده باشد.',
    check: [
      'پایین‌ترین عقربه‌ی کوچکِ زیرِ «محتوا» را پیدا کن؛ عقربه‌ی بزرگ میانگینِ همان‌هاست.',
      'بی‌عکس، بی‌مختصات، بی‌آدرس، ساعتِ نامعلوم و قیمتِ نامعلوم هر کدام دو عقربه دارند — «کل» و «بدترین شهر» — و در این میانگین بدترینِ آن دو حساب می‌شود. پس اتاوای بی‌مختصات «محتوا» و «کلِ سیستم» را پایین می‌کشد، حتی اگر عددِ کلِ همه‌ی شهرها سبز باشد.',
      'دکمه‌ی i همان عقربه‌ی کوچک دقیق می‌گوید کجا را ببینی.',
    ],
    fix: [
      'بیشترِ خرابی‌های محتوا از یک یا دو منبع می‌آید و درمانش در کرالر است؛ سربرگ‌های «عکس‌ها» و «آدرس‌ها» منبعِ مقصر را نشان می‌دهند.',
    ],
    links: [TAB.photos, TAB.addresses, TAB.review],
  },
  app: {
    meaning: 'خودِ سایت و اپِ موبایل کار می‌کنند یا نه: خطا می‌دهند؟ کسی بازشان می‌کند؟ هنوز از هر دو خبر می‌رسد؟',
    example: 'اگر بعد از یک به‌روزرسانی اپ موقعِ باز شدن بسته شود، هیچ‌کس به ما زنگ نمی‌زند. فقط ضربانِ اپ ساکت می‌شود و نشست‌ها پایین می‌آیند — این عقربه همان سکوت را می‌بیند.',
    check: [
      'پایین‌ترین عقربه‌ی کوچکِ زیرِ «سایت و اپ» را پیدا کن.',
      'اگر خطاها بالاست، مشکل فنی است؛ اگر فقط نشست‌ها پایین است و خطا نیست، شاید فقط کاربر کم آمده.',
    ],
    fix: [
      'دکمه‌ی i همان عقربه‌ی کوچک را باز کن؛ اگر خرابی بعد از یک انتشار شروع شده، برگرداندنِ همان انتشار سریع‌ترین درمان است.',
    ],
    links: [TAB.activity],
  },
  db: {
    meaning: 'دیتابیس — جایی که همه‌ی داده‌ها هست — سرِ پاست، سریع جواب می‌دهد و کارهای زمان‌بندی‌شده‌اش را انجام می‌دهد.',
    example: 'دیتابیس مثلِ انبارِ یک فروشگاه است: اگر درش شلوغ شود یا انباردار کند شود، ویترین (اپ و سایت) هم خالی یا کند می‌شود، حتی اگر خودِ ویترین هیچ ایرادی نداشته باشد.',
    check: [
      'پایین‌ترین عقربه‌ی کوچکِ زیرِ «دیتابیس» را پیدا کن.',
      'دو عقربه‌ی کوئری فقط وقتی عدد دارند که تو در هفته‌ی گذشته با حسابِ خودت اپ یا سایت را باز کرده باشی؛ وگرنه «نامعلوم»اند و این خرابی نیست.',
    ],
    fix: [
      'دکمه‌ی i همان عقربه‌ی کوچک را باز کن. برای بیشترشان `npm run log:pull` اولین قدم است.',
    ],
  },
  crawler: {
    meaning: 'کرالر — برنامه‌ای که هر شب سایت‌های رویداد را می‌گردد و رویدادهای تازه را می‌آورد — دیشب درست کار کرده یا نه.',
    example: 'اگر کرالر سه شب نچرخد، اپ ظاهراً سالم است و هیچ خطایی نمی‌دهد، ولی رویدادهای این آخرِ هفته اصلاً در آن نیستند. این عقربه برای دیدنِ همین خرابیِ بی‌صداست.',
    check: [
      'پایین‌ترین عقربه‌ی کوچکِ زیرِ «کرالر» را پیدا کن.',
      'سربرگِ «خطِ لوله‌ی کرالر» جزئیاتِ آخرین اجرا را دارد.',
    ],
    fix: [
      'درمانِ تقریباً همه‌ی این عقربه‌ها در مخزنِ کرالر است، نه این‌جا؛ دکمه‌ی i هر عقربه‌ی کوچک می‌گوید کدام کار.',
    ],
    links: [TAB.crawler, TAB.sources],
  },
};

/** راهنمای عقربه‌ی «کلِ سیستم». */
export const OVERALL_GUIDE: GaugeGuide = {
  meaning: 'حالِ ضعیف‌ترین بخشِ سیستم. عمداً میانگین نیست: سیستم همان‌قدر سالم است که بیمارترین تکه‌اش.',
  example: 'زنجیر به اندازه‌ی ضعیف‌ترین حلقه‌اش محکم است. اگر محتوا، سایت و دیتابیس عالی باشند ولی کرالر سه روز نچرخیده باشد، این عقربه قرمز است — چون کاربر در عمل رویدادهای کهنه می‌بیند.',
  check: [
    'از چهار عقربه‌ی بزرگِ کنارش، پایین‌ترین را پیدا کن؛ عددِ این عقربه دقیقاً همان است.',
    'زیرِ همان حوزه، عقربه‌ی کوچکِ زرد یا قرمز را پیدا کن.',
    'در «محتوا»، عقربه‌ی «بدترین شهر» هم در حکم اثر دارد: یک شهرِ خراب می‌تواند این عقربه را پایین بکشد، حتی وقتی عددِ کلِ همه‌ی شهرها سالم است.',
  ],
  fix: [
    'دکمه‌ی i روی همان عقربه‌ی کوچک می‌گوید چه چیزی را چک کنی و چطور درستش کنی.',
  ],
};

export const LEVEL_LABEL: Record<Level, string> = {
  ok: 'سالم', warn: 'مشکوک', bad: 'خراب', unknown: 'نامعلوم',
};

/**
 * مرزِ رنگ‌ها روی امتیازِ ۰..۱۰۰. یک جا، تا عقربه‌ی کل و عقربه‌ی هر
 * حوزه و هر عددِ تکی همه یک زبان داشته باشند.
 */
export const LEVEL_BANDS = { ok: 70, warn: 35 } as const;

export function levelOf(score: number | null): Level {
  if (score == null) return 'unknown';
  if (score >= LEVEL_BANDS.ok) return 'ok';
  if (score >= LEVEL_BANDS.warn) return 'warn';
  return 'bad';
}

/**
 * امتیازِ خطی بینِ دو آستانه، بریده به ۰..۱۰۰.
 *
 * `good < bad` یعنی کمتر بهتر است. برابر بودنشان معنی ندارد و null می‌دهد
 * تا یک آستانه‌ی غلط بی‌صدا «همه سبز» نسازد.
 */
export function scoreOf(value: number | null, good: number, bad: number): number | null {
  // بی‌نهایت معتبر است (بارِ شهرِ بی رویدادِ تازه) و به یکی از دو سر می‌چسبد.
  if (value == null || Number.isNaN(value) || good === bad) return null;
  const t = (value - bad) / (good - bad);
  return Math.round(Math.min(1, Math.max(0, t)) * 100);
}

/**
 * راهنمای دکمه‌ی «i» روی هر عقربه — برای کسی که عقربه را قرمز می‌بیند و
 * نمی‌داند از کجا شروع کند. عمداً ساده و بی‌اصطلاح نوشته شده، با یک مثالِ
 * دنیای واقعی؛ `help` همچنان تعریفِ فنیِ کوتاه است (تولتیپِ روی اسم).
 *
 * متنِ داخلِ بک‌تیک در صفحه به شکلِ کد (چپ‌به‌راست، قابلِ کپی) رندر می‌شود.
 * هر قدمِ `check`/`fix` باید به جایی اشاره کند که **واقعاً هست** (سربرگ،
 * دستور، جدول) — راهنمایی که آدم را به جای ناموجود بفرستد بدتر از نبودنش است.
 */
export interface GaugeGuide {
  /** یک جمله: این عدد به زبانِ ساده چه می‌شمارد. */
  meaning: string;
  /** همان، با یک مثالِ دنیای واقعی — چه چیزی را کاربر واقعاً تجربه می‌کند. */
  example: string;
  /** اگر زرد یا قرمز بود، کجا را نگاه کنم — به ترتیب. */
  check: string[];
  /** و چه کنم تا درست شود. */
  fix: string[];
  /**
   * میان‌برهای قابلِ کلیک به همان جاهایی که `check` نام می‌برد. سربرگ‌های
   * ادمین با `?tab=…` (همان پارامتری که `useUrlState` می‌خواند) و در تبِ تازه
   * باز می‌شوند تا صفحه‌ی سلامت سرِ جایش بماند.
   */
  links?: GaugeLink[];
  /**
   * اگر هست، پنجره تا ۱۰ ردیفِ واقعی پشتِ همین عدد را از
   * `admin_health_samples` (مایگریشن 0154) می‌آورد، و این جمله می‌گوید آن
   * ردیف‌ها چه هستند.
   */
  samples?: string;
}

export interface GaugeLink {
  label: string;
  href: string;
}

/** جوابِ `admin_health_exclusions` (مایگریشن 0157). */
export interface HealthExclusions {
  excluded: { city_id: number | null; country_code: string | null; city: string | null; city_country: string | null }[];
  cities: { id: number; name: string; country_code: string; users: number }[];
}

/** یک ردیف از `admin_health_samples` — یک شکل برای همه‌ی عقربه‌ها. */
export interface HealthSample {
  title: string;
  detail?: string | null;
  url?: string | null;
  at?: string | null;
  n?: number | null;
}

export interface GaugeSpec {
  key: string;
  group: GroupKey;
  label: string;
  help: string;
  guide: GaugeGuide;
  unit: Unit;
  /** مقدار از واقعیت‌ها. `at` لحظه‌ی همان عکس است، برای «چند ساعت از …». */
  read: (f: HealthFacts, at: number) => number | null;
  /** بی آستانه (`null`) یعنی فقط عدد و روند — عقربه ندارد. */
  good: number | null;
  bad: number | null;
  /** چرا الان سنجیدنی نیست — وقتی `read` تهی می‌دهد. */
  unknownWhy?: (f: HealthFacts, at: number) => string;
  /** یک خطِ توضیح زیرِ عدد (مثلاً نامِ شهری که عدد مالِ آن است). */
  note?: (f: HealthFacts) => string | null;
  /**
   * اگر هست، عقربه یک جفت دارد: «بدترین شهر» (WHOSUP-303) — همین عدد برای
   * یک شهر، از `content.by_city`.
   */
  perCity?: (c: HealthCityFacts) => number | null;
  /**
   * به‌جای `perCity` روی شهرهای واجد، شهرها و عددشان را خودش می‌دهد — برای
   * «بارِ شهر»، که شهرهایش شهرهای دارای کاربرند نه دارای رویداد.
   * `size` عددی است که در فهرست کنارِ شهر می‌آید.
   */
  cityRows?: (f: HealthFacts) => CityRow[] | null;
  /**
   * حکم دارد ولی در میانگینِ حوزه (و در نتیجه در حکمِ کل) حساب نمی‌شود —
   * برای چیزی که کاربرِ اپ هرگز نمی‌بیند، مثلِ کندیِ صفحه‌های ادمین (WHOSUP-323).
   */
  advisory?: boolean;
}

export interface CityRow {
  city: string;
  country_code: string;
  value: number | null;
  size: number;
}

const pct = (part: number, whole: number): number | null =>
  (whole > 0 ? Math.round((part / whole) * 1000) / 10 : null);

/** کاربر ÷ رویدادِ تازه؛ بی کاربر نامعلوم، بی رویدادِ تازه بی‌نهایت. */
const loadRatio = (users: number | null | undefined, fresh: number | null | undefined): number | null => {
  if (users == null || fresh == null || users === 0) return null;
  return fresh === 0 ? Infinity : Math.round((users / fresh) * 100) / 100;
};

const hoursSince =(iso: string | null | undefined, at: number): number | null => {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return null;
  return Math.max(0, Math.round(((at - t) / 3_600_000) * 10) / 10);
};

/** لاگِ فعالیت این هفته چیزی ندارد؟ آن‌وقت هر عددِ کوئری «صفر» نیست، «نیست». */
const noDevlog = (f: HealthFacts) => f.db.devlog_rows_7d === 0;
const NO_DEVLOG_WHY = 'لاگِ فعالیت (dev_log) در ۷ روزِ گذشته خالی است — حسابِ صاحبِ ابزار روی هیچ بیلدی وارد نبوده، پس چیزی برای سنجیدن نیست.';
const NO_CRAWLER_WHY = 'این دیتابیس اسکیمای crawler را ندارد؛ آن را مخزنِ event_crawler می‌سازد.';

/**
 * عقربه‌ی «آخرین اجرای کامل» دو شاهد دارد (WHOSUP-289): ثبتِ اجرا در
 * `crawler.pipeline_runs`، و رویدادهای تازه در `events`. روی ابر ثبتِ اجرا
 * از ۲۰۲۶-۰۹-۰۷ نمی‌رسد ولی هر روز صدها رویداد می‌رسد؛ بی شاهدِ دوم عقربه
 * «خراب» دروغ می‌گفت.
 *
 *   ثبتِ اجرا تازه (کمتر از `bad`)        → همان حکمِ قبلی
 *   ثبت کهنه، رویدادِ تازه در ۴۸ ساعت     → نامعلوم (از میانگین بیرون)
 *   هر دو کهنه                            → همان عددِ کهنه، یعنی خراب
 */
export const RUN_LOG_STALE_HOURS = 72;
export const DATA_FRESH_HOURS = 48;
const runLogStale = (f: HealthFacts) =>
  (f.crawler.hours_since_run ?? Infinity) >= RUN_LOG_STALE_HOURS;
const dataFresh = (f: HealthFacts, at: number) => {
  const h = hoursSince(f.content.last_created_at, at);
  return h != null && h <= DATA_FRESH_HOURS;
};
const RUN_LOG_MISSING_WHY = 'ثبتِ اجرا نمی‌رسد، ولی داده می‌رسد: در crawler.pipeline_runs اجرای کاملِ تازه‌ای نیست، ولی در ۴۸ ساعتِ گذشته رویدادِ تازه ساخته شده — پس کرالر احتمالاً می‌چرخد و فقط ثبتِ اجرایش به این دیتابیس نمی‌رسد.';

/**
 * «بدترین شهر» (WHOSUP-303): کدام شهرها حساب می‌شوند. شهری با چند رویداد
 * با یک رویدادِ بی‌مختصات ۵۰٪ می‌شود و همیشه «بدترین» بود؛ پس فقط شهرهای
 * دارای دست‌کم `CITY_MIN_LIVE` رویدادِ زنده — به‌جز شهرهای `ALWAYS_COUNTED_CITIES`،
 * که باید کامل باشند و با هر تعدادی شمرده می‌شوند.
 */
export const CITY_MIN_LIVE = 20;
export const ALWAYS_COUNTED_CITIES: readonly { city: string; country_code: string }[] = [
  { city: 'Ottawa', country_code: 'CA' },
];

const alwaysCounted = (c: HealthCityFacts) =>
  ALWAYS_COUNTED_CITIES.some((a) => a.city === c.city && a.country_code === c.country_code);

/** شهرهای واجدِ شرط؛ `null` یعنی این عکس `by_city` ندارد (پیش از 0161). */
export function countedCities(f: HealthFacts): HealthCityFacts[] | null {
  const all = f.content.by_city;
  if (!Array.isArray(all)) return null;
  return all.filter((c) => c.live > 0 && (c.live >= CITY_MIN_LIVE || alwaysCounted(c)));
}

/** راهنمای مشترکِ جفتِ «بدترین شهر»، برای `check` هر عقربه‌ی محتوای شهری. */
const CITY_CHECK = `عقربه‌ی دوم «بدترین شهر» است و نامِ شهر زیرش آمده: همین عدد برای شهری که از همه بدتر است. فقط شهرهایی حساب می‌شوند که دست‌کم ${CITY_MIN_LIVE} رویدادِ زنده دارند — اتاوا همیشه، با هر تعدادی. زیرش نوشته چند شهر از همین شهرها سالم‌اند، و «بیشتر» فهرستِ همه‌شان را از بد به خوب باز می‌کند.`;
const CITY_VERDICT = 'حکمِ «محتوا» و «کلِ سیستم» بدترینِ دو عقربه‌ی «کل» و «بدترین شهر» را حساب می‌کند؛ پس یک شهرِ خراب به‌تنهایی آن‌ها را پایین می‌کشد، حتی اگر عددِ کل سبز باشد.';

/**
 * فهرستِ عقربه‌ها — تنها جایی که آستانه‌ها نوشته شده‌اند.
 *
 * ⚠ آستانه‌ها حکمِ امروزند و نه قانونِ طبیعت: ۶۸۱ بی‌مختصات از ۲۹۴۱ (۲۳٪)
 * در ۲۰۲۶-۰۹-۲۶ «مشکوک» می‌شود، که همان چیزی است که باید باشد — نه قرمزِ
 * فریادزن، نه سبزِ بی‌خیال. وقتی ژئوکدینگ جلو رفت، آستانه را سخت‌تر کن.
 */
export const GAUGES: readonly GaugeSpec[] = [
  // ─── محتوا ───
  {
    key: 'photo_missing', group: 'content', label: 'بی‌عکس', unit: 'pct',
    guide: {
      meaning: 'از هر صد رویدادی که کاربر می‌بیند، چندتا جای عکسشان خالی است.',
      example: '۱۰٪ یعنی از هر ده کارتی که کاربر در «کشف» ورق می‌زند، یکی فقط یک مستطیلِ خاکستری است. مثلِ ویترینی که روی یک‌دهمِ جنس‌هایش فقط برچسبِ قیمت هست — کسی رویشان مکث نمی‌کند.',
      check: [
        CITY_CHECK,
        CITY_VERDICT,
        'سربرگِ «عکس‌ها»: کدام منبع بیشترین رویدادِ بی‌عکس را دارد. معمولاً یکی دو منبع بیشترِ عدد را می‌سازند.',
        'همان منبع را در سربرگِ «منابع» باز کن و «تست» بزن؛ در قدمِ استخراجِ فیلدها ببین عکسی پیدا می‌شود یا نه.',
        'اگر عدد یک‌شبه پرید، سایتِ آن منبع احتمالاً ظاهرِ صفحه‌اش را عوض کرده.',
      ],
      fix: [
        'اگر عکس روی صفحه‌ی منبع هست ولی ما نمی‌گیریمش: استخراج‌کننده‌ی همان منبع در مخزنِ کرالر باید اصلاح شود.',
        'اگر عکس‌ها را خودت «غیر معتبر» زده‌ای، در کارتِ «عکس‌های غیر معتبر» سربرگِ «عکس‌ها» با یک تیک برمی‌گردند.',
        'اگر منبع اصلاً عکس نمی‌دهد، این عدد برای آن منبع پایین نمی‌آید؛ یا بپذیرش یا منبع را کنار بگذار.',
      ],
      links: [TAB.photos, TAB.sources],
      samples: 'تازه‌ترین رویدادهای زنده‌ی بی‌عکس، با منبعشان؛ عنوان به صفحه‌ی خودِ رویداد در سایتِ منبع می‌رود تا ببینی عکس آن‌جا هست یا نه.',
    },
    help: 'سهمِ رویدادهای زنده‌ای که یا عکس ندارند یا عکسشان «غیر معتبر» علامت خورده. برای کاربر هر دو یک چیزند: مستطیلِ خالی.',
    read: (f) => pct(f.content.no_photo, f.content.live_events), good: 3, bad: 25,
    perCity: (c) => pct(c.no_photo, c.live),
  },
  {
    key: 'photo_broken', group: 'content', label: 'عکسِ لودنشده · ۷ روز', unit: 'count',
    guide: {
      meaning: 'چند عکسِ مختلف در هفت روزِ گذشته روی گوشی یا مرورگرِ یک کاربرِ واقعی باز نشده.',
      example: 'در دیتابیس همه چیز درست است: رویداد آدرسِ عکس دارد. ولی سایتِ منبع عکس را پاک کرده یا نمی‌گذارد سایت‌های دیگر نشانش بدهند. کاربر مستطیلِ خالی می‌بیند و کرالر هیچ خبری ندارد — فقط گوشیِ کاربر می‌فهمد و بی‌صدا به ما گزارش می‌دهد.',
      check: [
        CITY_CHECK,
        CITY_VERDICT,
        'عکسی که آدرسش به هیچ رویدادی نمی‌خورد در هیچ شهری شمرده نمی‌شود؛ پس جمعِ شهرها ممکن است از عددِ کل کمتر باشد.',
        'سربرگِ «عکس‌ها»، ستونِ «لود نشد»: فهرستِ همین عکس‌ها با منبعشان.',
        'بندانگشتیِ همان‌جا اگر ✕ است، از مرورگرِ خودت هم باز نمی‌شود؛ یعنی خرابی واقعی است، نه اینترنتِ ضعیفِ یک کاربر.',
        'اگر بیشترشان از یک منبع‌اند، آن سایت احتمالاً جلوی نمایشِ عکس‌هایش در سایت‌های دیگر را گرفته.',
      ],
      fix: [
        'همان‌جا تیکِ «معتبر» را از عکسِ خراب بردار تا دیگر به کاربر نشان داده نشود (پاک نمی‌شود، برمی‌گردد).',
        'اگر یک منبع مدام این‌طور است، برای کرالر تیکت بزن: عکسِ آن منبع باید از جای دیگری از صفحه برداشته یا پیشِ خودمان نگه داشته شود.',
      ],
      links: [TAB.photos],
      samples: 'عکس‌هایی که در ۷ روزِ گذشته بیشتر از همه لود نشده‌اند و چند بار؛ روی آدرس بزن تا ببینی از مرورگرِ تو باز می‌شود یا نه.',
    },
    help: 'چند آدرسِ عکسِ متفاوت در هفت روزِ گذشته روی دستگاهِ کاربر لود نشده (reportBrokenImage → client_errors). این همان چیزی است که از سمتِ کرالر دیده نمی‌شود.',
    read: (f) => f.content.broken_image_urls_7d, good: 0, bad: 20,
    perCity: (c) => c.broken_image_urls_7d ?? null,
  },
  {
    key: 'geo_missing', group: 'content', label: 'بی‌مختصات', unit: 'pct',
    guide: {
      meaning: 'از هر صد رویدادِ زنده، چندتا نقطه‌ای روی نقشه ندارند.',
      example: 'آدرسِ رویداد «سالنِ شهر» است ولی کسی آن را به طول و عرضِ جغرافیایی تبدیل نکرده. پس روی نقشه نمی‌نشیند و برای کسی که «نزدیکِ من» را می‌گردد انگار وجود ندارد. کم شدنِ این عدد یعنی کرالر در پیدا کردنِ مکان‌ها بهتر شده.',
      check: [
        CITY_CHECK,
        CITY_VERDICT,
        'سربرگِ «آدرس‌ها»: کدام منبع‌ها بیشترین رویدادِ بی‌مختصات را دارند و آدرسشان چه شکلی است.',
        'سربرگِ «خطِ لوله‌ی کرالر»: قدمِ تبدیلِ آدرس به مختصات دیشب اجرا شده یا خطا داده.',
        'اگر عدد ناگهان بالا رفت و بقیه‌ی عقربه‌های کرالر هم قرمزند، مشکل از اجرای شبانه است نه از آدرس‌ها.',
      ],
      fix: [
        'آدرس‌های مبهم (فقط نامِ سالن، بی خیابان) در مخزنِ کرالر، در `registry/lexicon.yaml`، به یک مکانِ مشخص وصل می‌شوند.',
        'اگر قدمِ مختصات‌یابی خطا داده (مثلاً سهمیه‌ی سرویسِ نقشه تمام شده)، همان قدم را در مخزنِ کرالر دوباره اجرا کن.',
      ],
      links: [TAB.addresses, TAB.crawler],
      samples: 'تازه‌ترین رویدادهای زنده‌ی بی‌مختصات، با منبع و آدرسی که دارند — آدرسِ مبهم را همین‌جا می‌بینی.',
    },
    help: 'سهمِ رویدادهای زنده‌ای که نه روی مکانشان مختصات دارند نه روی خودِ ردیف — روی نقشه نمی‌نشینند. کم شدنش یعنی ژئوکدینگِ کرالر کار می‌کند.',
    read: (f) => pct(f.content.no_geo, f.content.live_events), good: 5, bad: 40,
    perCity: (c) => pct(c.no_geo, c.live),
  },
  {
    key: 'address_missing', group: 'content', label: 'بی‌آدرس', unit: 'pct',
    guide: {
      meaning: 'از هر صد رویدادِ زنده، چندتا هیچ آدرسی ندارند — نه خودشان، نه مکانشان.',
      example: 'کارتی که می‌گوید «کنسرت، جمعه ساعتِ ۸» ولی نمی‌گوید کجا. کاربر نمی‌تواند تصمیم بگیرد برود یا نه، و به دوستش هم نمی‌تواند بگوید کجا ببینند هم را.',
      check: [
        CITY_CHECK,
        CITY_VERDICT,
        'سربرگِ «آدرس‌ها»: رویدادهای بی‌آدرس به تفکیکِ منبع.',
        'رویدادِ آنلاین طبیعی است آدرس نداشته باشد؛ اگر بیشترِ عدد از یک منبعِ آنلاین است، مشکلی نیست.',
        'با «تست»ِ همان منبع در سربرگِ «منابع» ببین آدرس در صفحه‌ی خودِ رویداد هست یا نه.',
      ],
      fix: [
        'اگر آدرس در صفحه هست و گرفته نمی‌شود: استخراج‌کننده‌ی آن منبع در مخزنِ کرالر باید اصلاح شود.',
        'برای چند رویدادِ مهم، می‌شود رویداد را با شماره‌اش در سربرگِ «صفِ بازبینی» باز کرد و آدرس را دستی نوشت.',
      ],
      links: [TAB.addresses, TAB.sources, TAB.review],
      samples: 'تازه‌ترین رویدادهای زنده‌ی بی‌آدرس، با منبعشان؛ عنوان به صفحه‌ی رویداد در سایتِ منبع می‌رود.',
    },
    help: 'سهمِ رویدادهای زنده‌ای که نه روی ردیف آدرسی دارند نه روی مکانشان.',
    read: (f) => pct(f.content.no_address, f.content.live_events), good: 3, bad: 25,
    perCity: (c) => pct(c.no_address, c.live),
  },
  {
    key: 'time_unknown', group: 'content', label: 'ساعتِ نامعلوم', unit: 'pct',
    guide: {
      meaning: 'از هر صد رویدادِ زنده، چندتا فقط تاریخ دارند و ساعتشان معلوم نیست — کاربر روی کارت «۰۰:۰۰» می‌بیند.',
      example: 'کنسرتی که منبعش فقط «۱۲ نوامبر» نوشته، در اپ «۱۲ نوامبر، ۰۰:۰۰» نشان داده می‌شود. کاربر یا فکر می‌کند نیمه‌شب است، یا نمی‌داند کی برود — و نمی‌تواند با دوستش قرار بگذارد. رویدادی که منبعش گفته «تمام‌روز» (مثلِ نمایشگاه) این‌جا شمرده نمی‌شود، چون ساعتش واقعاً معلوم است.',
      check: [
        CITY_CHECK,
        CITY_VERDICT,
        'این عدد از برچسبِ خودِ کرالر می‌آید (`time_precision = date` در `events.metadata`)، نه از حدس زدنِ ساعتِ ۰۰:۰۰. کرالر نیمه‌شب را به وقتِ محلیِ هر منبع می‌سنجد. رویدادی که کاربر یا ادمین ساخته برچسب ندارد و همیشه «ساعت‌دار» حساب می‌شود.',
        '«فقط تاریخ» یعنی منبع ساعت نداده، نه این‌که کرالر اشتباه کرده. نمونه‌ها را باز کن و صفحه‌ی منبع را ببین: اگر ساعت آن‌جا هست، کرالر نگرفته‌اش؛ اگر نیست، از این منبع بیشتر از این نمی‌شود گرفت.',
        'اگر بیشترِ نمونه‌ها از یک منبع‌اند، آن منبع را در سربرگِ «منابع» باز کن و «تست» بزن؛ در قدمِ صفحه‌ی خودِ رویداد ببین ساعت پیدا می‌شود یا نه.',
      ],
      fix: [
        'اگر ساعت روی صفحه‌ی خودِ رویداد هست ولی گرفته نمی‌شود: استخراج‌کننده‌ی همان منبع در مخزنِ کرالر باید اصلاح شود.',
        'اگر منبع واقعاً ساعت نمی‌دهد، این عدد برای آن منبع پایین نمی‌آید؛ یا بپذیرش، یا منبعِ بهتری برای همان رویدادها پیدا کن.',
      ],
      links: [TAB.sources],
      samples: 'تازه‌ترین رویدادهای زنده‌ی بی‌ساعت، با منبعشان و تاریخی که منبع داده؛ عنوان به صفحه‌ی رویداد در سایتِ منبع می‌رود تا ببینی ساعت آن‌جا هست یا نه.',
    },
    help: 'سهمِ رویدادهای زنده‌ای که کرالر برچسبِ «فقط تاریخ» (metadata.time_precision = date) زده: ساعتِ شروع به وقتِ محلی ۰۰:۰۰ است چون منبع ساعتی نداده. «تمام‌روز» (all_day) و رویدادهای بی برچسب حساب نمی‌شوند.',
    read: (f) => (f.content.no_time == null ? null : pct(f.content.no_time, f.content.live_events)),
    good: 5, bad: 30,
    unknownWhy: () => 'شمارشِ ساعتِ نامعلوم (مایگریشنِ 0189) روی این دیتابیس اجرا نشده، یا رویدادِ زنده‌ای نیست.',
    perCity: (c) => (c.no_time == null ? null : pct(c.no_time, c.live)),
  },
  {
    key: 'price_unknown', group: 'content', label: 'قیمتِ نامعلوم', unit: 'pct',
    guide: {
      meaning: 'از هر صد رویدادِ زنده، چندتا نه «رایگان»اند و نه قیمتی دارند — نمی‌دانیم کاربر باید پول بدهد یا نه.',
      example: 'کاربر می‌خواهد دوستانش را به یک کارگاه ببرد و اولین سوالشان این است که «چقدر می‌شود؟». اگر کارت چیزی نگوید، یا باید به سایتِ منبع برود، یا بی‌خیال می‌شود. رویدادی که منبعش «Free» یا «$25» یا حتی «هرچه می‌توانید» نوشته، این‌جا شمرده نمی‌شود.',
      check: [
        CITY_CHECK,
        CITY_VERDICT,
        'این عدد از برچسبِ خودِ کرالر می‌آید (`price_status = unknown` در `events.metadata`)؛ متن‌های بی‌معنی مثلِ «TBA» یا «See listing» را کرالر خودش «نامعلوم» حساب می‌کند. برای رویدادِ بی برچسب همان قاعده تکرار می‌شود: نه رایگان، نه متنِ قیمت.',
        'نمونه‌ها را باز کن و صفحه‌ی منبع را ببین: اگر قیمت آن‌جا هست، کرالر نگرفته‌اش؛ اگر نیست، منبع قیمت نمی‌دهد.',
        'اگر بیشترِ نمونه‌ها از یک منبع‌اند، همان منبع را در سربرگِ «منابع» با «تست» امتحان کن؛ در قدمِ استخراجِ فیلدها ببین قیمت پیدا می‌شود یا نه.',
      ],
      fix: [
        'اگر قیمت روی صفحه‌ی منبع (یا صفحه‌ی خریدِ بلیت) هست ولی گرفته نمی‌شود: استخراج‌کننده‌ی همان منبع در مخزنِ کرالر باید اصلاح شود.',
        'اگر منبع واقعاً قیمت نمی‌دهد، این عدد برای آن منبع پایین نمی‌آید؛ یا بپذیرش، یا منبعِ بهتری برای همان رویدادها پیدا کن.',
      ],
      links: [TAB.sources],
      samples: 'تازه‌ترین رویدادهای زنده‌ی بی‌قیمت، با منبعشان؛ عنوان به صفحه‌ی رویداد در سایتِ منبع می‌رود تا ببینی قیمت آن‌جا هست یا نه.',
    },
    help: 'سهمِ رویدادهای زنده با metadata.price_status = unknown (نه رایگان، نه متنِ قیمتِ معنی‌دار)؛ رویدادِ بی برچسب: نه is_free و نه price_note.',
    read: (f) => (f.content.no_price == null ? null : pct(f.content.no_price, f.content.live_events)),
    good: 15, bad: 60,
    unknownWhy: () => 'شمارشِ قیمتِ نامعلوم (مایگریشنِ 0189) روی این دیتابیس اجرا نشده، یا رویدادِ زنده‌ای نیست.',
    perCity: (c) => (c.no_price == null ? null : pct(c.no_price, c.live)),
  },
  {
    key: 'stale', group: 'content', label: 'رویدادِ کهنه', unit: 'count',
    guide: {
      meaning: 'رویدادهای یک‌باره‌ای که بیش از یک روز از تمام شدنشان گذشته ولی هنوز به کاربر نشان داده می‌شوند.',
      example: 'کاربر امروز «جشنواره‌ی پریروز» را در فهرست می‌بیند، ذوق می‌کند، بازش می‌کند و می‌فهمد تمام شده. یک بار کافی است تا به کلِ فهرست بی‌اعتماد شود. این عدد باید صفر بماند.',
      check: [
        CITY_CHECK,
        CITY_VERDICT,
        'عقربه‌ی «ساعت از آخرین اجرای کامل» در همین صفحه: اگر آن هم قرمز است، علت همان است — کنار گذاشتنِ رویدادهای تمام‌شده کارِ اجرای شبانه‌ی کرالر است و این مخزن کدی برایش ندارد.',
        'سربرگِ «خطِ لوله‌ی کرالر»: اجرای دیشب کامل شده یا وسطِ راه ایستاده.',
        'اگر اجرا سالم است ولی این عدد بالاست، تاریخِ پایانِ این رویدادها احتمالاً غلط خوانده شده.',
      ],
      fix: [
        'اگر اجرای شبانه نچرخیده، آن را در مخزنِ کرالر دوباره اجرا کن؛ کهنه‌ها خودشان کنار می‌روند.',
        'اگر تاریخ‌ها غلط‌اند، خواندنِ تاریخِ همان منبع در کرالر باید اصلاح شود.',
      ],
      links: [TAB.crawler],
      samples: 'رویدادهای کهنه‌ای که هنوز نشان داده می‌شوند، با تاریخِ پایانشان؛ عنوان را باز کن و تاریخ را با صفحه‌ی منبع مقایسه کن.',
    },
    help: 'رویدادِ تک‌جلسه‌ای که یک روز از پایانش گذشته و هنوز published است. باید صفر بماند؛ غیرِ صفر یعنی چرخه‌ی expire جا انداخته.',
    read: (f) => f.content.stale, good: 0, bad: 50,
    perCity: (c) => c.stale ?? null,
  },
  {
    key: 'review_queue', group: 'content', label: 'صفِ بازبینی', unit: 'count',
    guide: {
      meaning: 'چند رویداد منتظرند یک آدم تاییدشان کند تا به کاربر نشان داده شوند.',
      example: 'مثلِ صندوقِ نامه‌های نخوانده: پنج‌تا عادی است. پنجاه‌تا یعنی چند روز کسی سر نزده و رویدادهای خوبی پشتِ در مانده‌اند — بعضی‌شان شاید تا تایید شوند تمام هم شده باشند.',
      check: [
        CITY_CHECK,
        CITY_VERDICT,
        'صفِ شهری که هیچ رویدادِ زنده‌ای ندارد در فهرستِ شهرها نیست، ولی در عددِ کل هست.',
        'سربرگِ «صفِ بازبینی»: چندتا و از چه منبعی.',
        'اگر یک‌شبه پر شد، کرالر احتمالاً منبعی را که قبلاً مستقیم منتشر می‌شد به بازبینی فرستاده.',
      ],
      fix: [
        'صف را در سربرگِ «صفِ بازبینی» تایید یا رد کن.',
        'اگر همه از یک منبعِ مطمئن‌اند، بهتر است آن منبع در کرالر مستقیم منتشر شود تا صف دوباره پر نشود.',
      ],
      links: [TAB.review],
      samples: 'قدیمی‌ترین رویدادهایی که منتظرِ بازبینی‌اند، با منبعشان.',
    },
    help: 'رویدادهای pending_review. صفی که بزرگ می‌شود یعنی چیزی منتظرِ آدم مانده.',
    read: (f) => f.content.pending_review, good: 5, bad: 50,
    perCity: (c) => c.pending_review ?? null,
  },
  {
    key: 'reports_overdue', group: 'content', label: 'گزارشِ بازِ بیش از ۲۴ ساعت', unit: 'count',
    guide: {
      meaning: 'چند گزارشِ کاربر (سیگنال، پیام یا پروفایلِ ناپسند) بیش از ۲۴ ساعت است که باز مانده و کسی به آن رسیدگی نکرده.',
      example: 'در شرایطِ استفاده قول داده‌ایم هر گزارش ظرفِ ۲۴ ساعت بررسی شود، و اپل همین را می‌خواهد. یک گزارشِ دوروزه یعنی آن قول شکسته و محتوای آزارنده شاید هنوز جلوی چشمِ بقیه است.',
      check: [
        'سربرگِ «گزارش‌ها»: کدام‌ها باز مانده‌اند و از کِی.',
        'اگر خبرِ تلگرام یا ایمیلِ گزارشِ تازه نرسیده، کلیدهای telegram_bot_token و resend_api_key در private.config را ببین.',
      ],
      fix: [
        'در سربرگِ «گزارش‌ها» هر گزارش را با «حذفِ محتوا»، «اخراجِ کاربر» یا «چیزی نبود» ببند.',
      ],
      links: [TAB.reports],
    },
    help: 'content_reports با status = open که created_at آن بیش از ۲۴ ساعت پیش است. هر عددِ بالای صفر یعنی قولِ ۲۴ ساعت شکسته. عکس‌های پیش از مایگریشن 0160 نامعلوم‌اند.',
    read: (f) => f.content.overdue_reports ?? null, good: 0, bad: 1,
    unknownWhy: () => 'مایگریشنِ 0160 روی این دیتابیس اجرا نشده.',
    note: (f) => (f.content.open_reports != null ? `${f.content.open_reports} گزارشِ باز در کل` : null),
  },
  {
    key: 'city_load', group: 'content', label: 'کاربر به ازای رویدادِ تازه', unit: 'ratio',
    guide: {
      meaning: 'به ازای هر رویدادِ تازه‌ی سه روزِ گذشته چند کاربرِ فعال (۶۰ روزِ گذشته) هست — عقربه‌ی اول برای همه‌ی شهرها روی هم، عقربه‌ی دوم برای شهری که وضعش از همه بدتر است، با نامِ همان شهر.',
      example: 'اگر در یک شهر ۴۰ نفر اپ را باز می‌کنند و در سه روزِ گذشته فقط دو رویدادِ تازه آنجا آمده، عدد ۲۰ است: هر کدام از آن ۴۰ نفر هر بار همان فهرستِ دیروز را می‌بیند. عددهای سراسریِ «رویدادِ تازه» این را پنهان می‌کنند، چون شهرِ دیگری ممکن است صدها رویدادِ تازه داشته باشد.',
      check: [
        'عقربه‌ی دوم «بدترین شهر» است. این‌جا برخلافِ عقربه‌های دیگرِ محتوا حداقلِ ۲۰ رویداد شرط نیست: هر شهری که کاربرِ فعال دارد حساب می‌شود، چون شهرِ بی‌رویداد با کاربر دقیقاً همان مشکلی است که این عقربه دنبالش است. «بیشتر» همه‌ی این شهرها را از بد به خوب باز می‌کند.',
        CITY_VERDICT,
        'نامِ شهرِ زیرِ عقربه‌ی دوم را ببین؛ در سربرگِ «منابع» چند منبع برای آن شهر هست و دیشب کدامشان رویداد آورده‌اند.',
        'سربرگِ «خطِ لوله‌ی کرالر»: منبع‌های همان شهر در آخرین اجرا شکست خورده‌اند یا نه.',
        'اگر عدد «∞» است، آن شهر در سه روزِ گذشته هیچ رویدادِ تازه‌ای نگرفته — کلاً بی‌خوراک مانده.',
        'اگر شهری آزمایشی است یا عمداً پوشش داده نمی‌شود، از «حذف از آمار» پایینِ همین صفحه کنارش بگذار.',
      ],
      fix: [
        'اگر منبع‌های آن شهر خراب‌اند، استخراج‌کننده‌شان در مخزنِ کرالر باید اصلاح شود.',
        'اگر شهر منبعِ کافی ندارد، در سربرگِ «منابع» برای آن شهر منبعِ تازه پیدا کن؛ اجرای شبانه هم شهرِ کم‌منبع را خودش برای کشفِ منبع انتخاب می‌کند.',
      ],
      links: [TAB.sources, TAB.crawler],
    },
    help: 'کاربرانِ فعال در ۶۰ روزِ گذشته ÷ رویدادهای تازه‌ی منتشرشده در ۳ روزِ گذشته — کل و بیشترینِ شهرها، بی حساب‌های داخلی و بی شهرها و کشورهای «حذف از آمار»؛ شهرِ کاربر از profiles.city_id. بی رویدادِ تازه بی‌نهایت (∞) است، یعنی خراب.',
    read: (f) => loadRatio(f.content.city_load_users_total, f.content.city_load_new_total),
    good: 2, bad: 20,
    unknownWhy: () => 'هیچ شهری کاربرِ فعالِ ۶۰ روزِ گذشته ندارد، یا شمارشِ کل (مایگریشنِ 0161) روی این دیتابیس اجرا نشده.',
    note: (f) => `${f.content.city_load_users_total ?? 0} کاربر، ${f.content.city_load_new_total ?? 0} رویدادِ تازه`,
    cityRows: (f) => {
      const all = f.content.city_loads;
      if (Array.isArray(all)) {
        return all.map((c) => ({ city: c.city, country_code: c.country_code, size: c.users_60d,
          value: loadRatio(c.users_60d, c.new_3d) }));
      }
      // عکس‌های پیش از 0161 فقط بدترین شهر را دارند — همان یک ردیف، تا روندِ قدیمی گم نشود.
      if (!f.content.city_load_city) return null;
      return [{ city: f.content.city_load_city, country_code: '', size: f.content.city_load_users_60d ?? 0,
        value: f.content.city_load_new_3d === 0 ? Infinity : (f.content.city_load_max ?? null) }];
    },
  },

  // ─── سایت و اپ ───
  {
    key: 'errors_24h', group: 'app', label: 'خطای اپ و سایت · ۲۴ ساعت', unit: 'count',
    guide: {
      meaning: 'چند خطا در ۲۴ ساعتِ گذشته از گوشی‌ها و مرورگرهای کاربران به ما رسیده (غیر از عکس‌های لودنشده، که جدا شمرده می‌شوند).',
      example: 'کاربری دکمه‌ی «می‌آیم» را می‌زند و هیچ اتفاقی نمی‌افتد. او چیزی به ما نمی‌گوید و فقط می‌رود؛ ولی اپ بی‌صدا یک گزارشِ خطا می‌فرستد. این عدد شمارِ همان گزارش‌هاست.',
      check: [
        'جدولِ «چند واقعیتِ بی‌حکم» پایینِ همین صفحه: خطاهای هفت روز، چند کاربرِ درگیر و زمانِ آخرین خطا.',
        'جدولِ `client_errors` در داشبوردِ سوپابیس: ستونِ `where_at` می‌گوید خطا از کجای اپ آمده و `message` چه گفته.',
        '`npm run log:pull` خطاهای خودت را گروه‌بندی‌شده و با تعداد نشان می‌دهد.',
        'اگر عدد درست بعد از یک انتشار پرید، همان انتشار مظنونِ اول است.',
      ],
      fix: [
        'پرتکرارترین خطا را پیدا کن و برایش تیکت بزن؛ معمولاً یکی دو خطا بیشترِ عدد را می‌سازند.',
        'اگر بعد از یک انتشار شروع شده و جدی است، انتشارِ قبلی را برگردان تا درست شود.',
      ],
      samples: 'پرتکرارترین خطاهای ۲۴ ساعتِ گذشته: کجای اپ (`where_at`)، چه پیامی، چند بار.',
    },
    help: 'ردیف‌های client_errors در ۲۴ ساعتِ گذشته، بی عکس‌های لودنشده (آن‌ها در «محتوا» شمرده شده‌اند). با حساب‌های داخلی: خطای روی گوشیِ خودمان هم خطاست.',
    read: (f) => f.app.errors_24h, good: 0, bad: 20,
  },
  {
    key: 'sessions_trend', group: 'app', label: 'نشست‌ها نسبت به هفته‌ی قبل', unit: 'pct',
    guide: {
      meaning: 'این هفته چند درصد بیشتر یا کمتر از هفته‌ی قبل اپ و سایت باز شده‌اند، بدونِ حساب‌های خودمان.',
      example: 'هفته‌ی قبل صد بار اپ باز شد و این هفته چهل بار؛ عقربه −۶۰٪ نشان می‌دهد. یا کاربرها رفته‌اند، یا اپ دیگر باز نمی‌شود، یا باز می‌شود ولی نمی‌تواند خبرش را به ما بدهد. عدد نمی‌گوید کدام — عقربه‌های کنارش می‌گویند.',
      check: [
        'سربرگِ «فعالیتِ کاربران»: افت از سایت است یا از اپ، و از کدام روز شروع شده.',
        'عقربه‌های «آخرین ضربان» کنارِ همین: اگر یکی از دو طرف کاملاً ساکت شده، خرابیِ فنی است نه رفتنِ کاربر.',
        'با عددهای کوچک (۵ نشست در برابرِ ۱۰) درصد خیلی بزرگ به نظر می‌رسد؛ عددِ خام را در جدولِ پایینِ صفحه هم ببین.',
      ],
      fix: [
        'اگر یک طرف ساکت شده: همان را خودت باز کن و ببین بالا می‌آید یا نه، و دکمه‌ی i عقربه‌ی ضربانِ همان طرف را بخوان.',
        'اگر فنی نیست و واقعاً کاربر کم آمده، این سوالِ محصول است نه باگ.',
      ],
      links: [TAB.activity],
      samples: 'نشست‌های هر روز در ۱۴ روزِ گذشته، بی حساب‌های خودمان — روزی که افت شروع شد این‌جا پیداست.',
    },
    help: 'درصدِ تغییرِ نشست‌های ۷ روزِ گذشته نسبت به ۷ روزِ قبل از آن، بی حساب‌های داخلی. صفر یعنی همان‌قدر؛ منفی یعنی افت.',
    read: (f) => (f.app.sessions_prev_7d > 0
      ? Math.round(((f.app.sessions_7d - f.app.sessions_prev_7d) / f.app.sessions_prev_7d) * 100)
      : null),
    good: 0, bad: -60,
    unknownWhy: () => 'در ۷ روزِ قبل از آن هیچ نشستی نبوده، پس نسبتی هم نیست.',
  },
  {
    key: 'web_pulse', group: 'app', label: 'آخرین ضربانِ سایت', unit: 'hours',
    guide: {
      meaning: 'چند ساعت از آخرین باری که کسی سایت را باز کرده و سایت خبرش را به ما داده گذشته.',
      example: 'مثلِ صدای قلب: اگر معمولاً هر چند ساعت یک نفر سایت را باز می‌کند و حالا دو روز هیچ صدایی نیامده، یا سایت پایین است، یا باز می‌شود ولی نمی‌تواند با دیتابیس حرف بزند، یا واقعاً کسی نیامده.',
      check: [
        'خودت سایت را باز کن (`https://whosup.ca`): بالا می‌آید؟ فهرستِ رویدادها پر است؟',
        'وضعیتِ دیپلوی در کلادفلر: `node scripts/pages.mjs status` — آخرین بیلدِ `main` سبز است؟',
        'سربرگِ «فعالیتِ کاربران»، فقط سایت: آخرین نشستِ سایت کی بوده.',
      ],
      fix: [
        'اگر سایت پایین است یا خالی است: آخرین دیپلویِ سالم را در کلادفلر برگردان.',
        'اگر سایت بالاست ولی ضربان نمی‌رسد: کنسولِ مرورگر را ببین که ثبتِ رویدادها خطا می‌دهد یا نه.',
        'اگر فقط کسی نیامده (شب، تعطیلی)، کاری لازم نیست.',
      ],
      links: [{ label: 'خودِ سایت', href: 'https://whosup.ca' }, TAB.activityWeb],
    },
    help: 'چند ساعت از آخرین رویدادی که وب به app_events فرستاده گذشته. ضربانِ کند یعنی یا کسی نمی‌آید یا سایت چیزی نمی‌فرستد — هر دو خبرند.',
    read: (f, at) => hoursSince(f.app.last_web_at, at), good: 6, bad: 48,
    unknownWhy: () => 'وب هنوز هیچ رویدادی به app_events نفرستاده.',
  },
  {
    key: 'mobile_pulse', group: 'app', label: 'آخرین ضربانِ اپ', unit: 'hours',
    guide: {
      meaning: 'چند ساعت از آخرین باری که کسی اپِ موبایل را باز کرده و اپ خبرش را به ما داده گذشته.',
      example: 'تسترهای گوگل‌پلی اپ را باز می‌کنند؛ اگر دو روز هیچ صدایی نیامد، یا اپ موقعِ باز شدن بسته می‌شود، یا آخرین به‌روزرسانی چیزی را خراب کرده، یا واقعاً کسی سراغش نرفته.',
      check: [
        'اپ را روی گوشیِ خودت باز کن. به‌روزرسانیِ تازه با دو بار بستن (کشیدن از فهرستِ برنامه‌های اخیر) و باز کردن می‌رسد.',
        'سربرگِ «فعالیتِ کاربران»، فقط اپ: آخرین نشستِ اپ کی بوده.',
        'آخرین به‌روزرسانیِ منتشرشده‌ی اپ کی بوده و چه تغییری داشته.',
      ],
      fix: [
        'اگر اپ بعد از یک به‌روزرسانی خراب شده، نسخه‌ی قبلی را دوباره منتشر کن.',
        'اگر فقط کسی نیامده، کاری لازم نیست.',
      ],
      links: [TAB.activityMobile],
    },
    help: 'همان، برای اپِ موبایل.',
    read: (f, at) => hoursSince(f.app.last_mobile_at, at), good: 6, bad: 48,
    unknownWhy: () => 'اپِ موبایل هنوز هیچ رویدادی به app_events نفرستاده.',
  },

  // ─── دیتابیس ───
  {
    key: 'connections', group: 'db', label: 'اتصال‌های باز', unit: 'pct',
    guide: {
      meaning: 'از همه‌ی خط‌هایی که دیتابیس هم‌زمان می‌تواند جواب بدهد، چند درصد الان اشغال است.',
      example: 'مثلِ خط‌های تلفنِ یک دفتر: اگر ۶۰ خط دارد و ۵۵تا اشغال است، تماسِ بعدی بوقِ اشغال می‌خورد — یعنی صفحه برای کاربر نمی‌آید یا خیلی دیر می‌آید.',
      check: [
        'جدولِ «چند واقعیتِ بی‌حکم» پایینِ همین صفحه: عددِ دقیقِ اتصال‌ها و سقف.',
        'کرالر وقتی می‌چرخد اتصال می‌گیرد؛ اگر عدد فقط هنگامِ اجرای شبانه بالاست، علت همان است.',
        'در ویرایشگرِ SQLِ سوپابیس ببین چه کسی این همه اتصال باز کرده: `select usename, application_name, state, count(*) from pg_stat_activity group by 1, 2, 3 order by 4 desc;`',
      ],
      fix: [
        'برنامه‌ای که اتصال را باز گذاشته (معمولاً یک اسکریپت یا کرالرِ گیرکرده) را ببند یا دوباره راه بینداز.',
        'اگر مصرفِ واقعی بالا رفته، سقف را در تنظیماتِ سوپابیس بالا ببر یا پلن را ارتقا بده.',
      ],
      samples: 'اتصال‌های همین لحظه، به تفکیکِ برنامه، کاربرِ دیتابیس و وضعیت — همان کوئریِ بالا، از پیش اجراشده.',
    },
    help: 'سهمِ اتصال‌های باز از سقفِ max_connections. نزدیکِ سقف یعنی درخواست‌های تازه منتظر می‌مانند یا رد می‌شوند.',
    read: (f) => pct(f.db.connections, f.db.max_connections), good: 30, bad: 85,
  },
  {
    key: 'failed_queries', group: 'db', label: 'کوئریِ ناموفق · ۷ روز', unit: 'count',
    guide: {
      meaning: 'چند درخواست از اپ و سایت به دیتابیس در هفت روزِ گذشته با خطا برگشته. فقط از وقت‌هایی که تو با حسابِ خودت وارد بوده‌ای.',
      example: 'صفحه‌ی دوستان را باز می‌کنی و خالی است، در حالی که دوست داری. پشتِ صحنه دیتابیس گفته «اجازه نداری» یا «این را نمی‌شناسم». این عدد همین «نه»های پنهان را می‌شمارد.',
      check: [
        '`npm run log:pull`: هر درخواستِ ناموفق را با نامش و پیامِ خطایش نشان می‌دهد.',
        'اگر پیام می‌گوید تابع یا ستونی وجود ندارد (`does not exist`): مایگریشنی روی این دیتابیس اجرا نشده.',
        'اگر پیام درباره‌ی اجازه است (`permission denied` یا `row-level security`): قانونِ دسترسیِ یک جدول جلوی کار را گرفته.',
      ],
      fix: [
        'مایگریشنِ جامانده را اجرا کن؛ `node scripts/db.mjs status` نشان می‌دهد کدام اجرا نشده.',
        'خطای اجازه یعنی باگِ واقعی؛ با پیامِ خطا تیکت بزن.',
      ],
      samples: 'درخواست‌هایی که در ۷ روزِ گذشته بیشتر از همه شکست خورده‌اند، با جزئیاتِ آخرین شکست.',
    },
    help: 'رفت‌وبرگشت‌هایی که با خطا برگشتند، از لاگِ فعالیت (dev_log). فقط هفته‌هایی که صاحبِ ابزار وارد بوده.',
    read: (f) => (noDevlog(f) ? null : f.db.failed_queries_7d), good: 0, bad: 10,
    unknownWhy: () => NO_DEVLOG_WHY,
  },
  {
    key: 'query_p95', group: 'db', label: 'کندیِ کوئری‌ها · p95', unit: 'ms',
    guide: {
      meaning: '۹۵٪ درخواست‌های اپ و سایت به دیتابیس از این عدد سریع‌تر جواب گرفته‌اند؛ یعنی کندترین‌ها چقدر کندند. درخواست‌های صفحه‌های ادمین (هر نامی که با `rpc.admin_` شروع شود) از ۱۳ اکتبرِ ۲۰۲۶ اینجا شمرده نمی‌شوند و عقربه‌ی جدای «کندیِ ادمین» را دارند؛ پس خطِ تاریخچه‌ی پیش از آن روز ادمین را هم داشته و با بعدش قابلِ مقایسه نیست.',
      example: 'اگر این عدد ۲۵۰۰ میلی‌ثانیه باشد، از هر بیست بار که صفحه‌ای را باز می‌کنی، یک بار دو ثانیه و نیم منتظرِ چرخیدنِ دایره می‌مانی — همان لحظه‌ای که آدم فکر می‌کند اپ هنگ کرده.',
      check: [
        '`npm run log:pull`: بخشِ کندترین کوئری‌ها می‌گوید کدام درخواست کند است.',
        'جدولِ «چند واقعیتِ بی‌حکم»: چند کوئری کندتر از ۲ ثانیه بوده.',
        'اگر همه با هم کند شده‌اند نه یکی، دیتابیس زیرِ بار است؛ عقربه‌ی «اتصال‌های باز» را هم ببین.',
      ],
      fix: [
        'برای کوئریِ کند تیکت بزن؛ معمولاً یک ایندکسِ جامانده است یا تابعی که کلِ یک جدولِ بزرگ را می‌خواند.',
        'اگر فقط هنگامِ اجرای شبانه‌ی کرالر کند است، زمانِ اجرای کرالر را از ساعتِ شلوغیِ کاربران دور کن.',
      ],
      samples: 'کندترین درخواست‌های غیرادمینیِ ۷ روزِ گذشته به ترتیبِ صدکِ ۹۵، با بیشترین زمان و تعدادشان.',
    },
    help: 'صدکِ ۹۵ِ مدتِ رفت‌وبرگشت‌های اپ و سایت در ۷ روزِ گذشته، از لاگِ فعالیت، بدونِ درخواست‌های ادمین. یعنی ۹۵٪ کوئری‌ها از این سریع‌تر بودند.',
    read: (f) => (noDevlog(f) ? null : f.db.query_p95_ms_7d), good: 400, bad: 2500,
    unknownWhy: (f) => (noDevlog(f) ? NO_DEVLOG_WHY : 'این هفته هیچ کوئریِ غیرادمینی‌ای در لاگِ فعالیت نیست.'),
  },
  {
    key: 'admin_query_p95', group: 'db', label: 'کندیِ ادمین · p95', unit: 'ms', advisory: true,
    guide: {
      meaning: 'همان «کندیِ کوئری‌ها»، فقط برای درخواست‌های صفحه‌های ادمین (هر نامی که با `rpc.admin_` شروع شود). کاربرِ اپ و سایت این صفحه‌ها را نمی‌بیند، پس این عقربه رنگ دارد ولی روی حکمِ «دیتابیس» و حکمِ کلِ سیستم اثر ندارد.',
      example: 'همین صفحه‌ی سلامت چند جدولِ بزرگ را می‌شمارد و ممکن است سه ثانیه طول بکشد. این برای تو آزاردهنده است، ولی هیچ کاربری منتظرش نمانده — برای همین از عقربه‌ی کاربران جدا شده تا کندیِ واقعیِ آن‌ها را پنهان نکند.',
      check: [
        'نمونه‌های همین عقربه: کدام تابعِ ادمین کندتر از همه است.',
        '`npm run log:pull`: بخشِ کندترین کوئری‌ها، با نامِ `rpc.admin_…`.',
        'جدولِ «چند واقعیتِ بی‌حکم»: چند درخواستِ ادمین کندتر از ۲ ثانیه بوده.',
      ],
      fix: [
        'اگر یک زبانه آن‌قدر کند است که کارت را کند می‌کند، برای همان تابع تیکت بزن؛ معمولاً ایندکس یا شمارشی است که می‌شود از پیش حساب کرد.',
        'اگر کند است ولی کارت راه می‌افتد، کاری لازم نیست.',
      ],
      samples: 'کندترین درخواست‌های ادمینِ ۷ روزِ گذشته به ترتیبِ صدکِ ۹۵، با بیشترین زمان و تعدادشان.',
    },
    help: 'صدکِ ۹۵ِ مدتِ درخواست‌های `rpc.admin_…` در ۷ روزِ گذشته. روی حکمِ کل اثر ندارد.',
    read: (f) => (noDevlog(f) ? null : (f.db.admin_query_p95_ms_7d ?? null)), good: 1500, bad: 6000,
    unknownWhy: (f) => (noDevlog(f) ? NO_DEVLOG_WHY
      : f.db.admin_query_p95_ms_7d === undefined ? 'مایگریشنِ ۰۱۶۳ روی این دیتابیس اجرا نشده.'
      : 'این هفته هیچ درخواستِ ادمینی در لاگِ فعالیت نیست.'),
    note: () => 'روی حکمِ کل اثر ندارد',
  },
  {
    key: 'cron_failed', group: 'db', label: 'کرونِ شکست‌خورده · ۲۴ ساعت', unit: 'count',
    guide: {
      meaning: 'کارهای زمان‌بندی‌شده‌ی خودِ دیتابیس که در ۲۴ ساعتِ گذشته شکست خورده‌اند.',
      example: 'دیتابیس هر پنج دقیقه اعلان‌های در صف را می‌فرستد. اگر این کار شکست بخورد هیچ‌کس خطایی نمی‌بیند — فقط دوستت هیچ‌وقت خبردار نمی‌شود که دعوتش کرده‌ای.',
      check: [
        'در ویرایشگرِ SQLِ سوپابیس: `select j.jobname, d.status, d.return_message, d.start_time from cron.job_run_details d join cron.job j using (jobid) where d.status <> \'succeeded\' order by d.start_time desc limit 20;`',
        'ستونِ `return_message` دلیلِ شکست را می‌گوید، و `jobname` کدام کار بوده.',
      ],
      fix: [
        'اگر پیام می‌گوید تابع یا ستونی نیست: مایگریشنی جا مانده؛ اجرایش کن (`node scripts/db.mjs status`).',
        'اگر خطای منطقی است، برای همان کار تیکت بزن. تا درست نشود، هر بار دوباره شکست می‌خورد.',
      ],
      samples: 'اجراهای شکست‌خورده‌ی ۲۴ ساعتِ گذشته: نامِ کار و پیامِ خطا.',
    },
    help: 'اجراهای شکست‌خورده‌ی pg_cron (ارسالِ اعلان‌ها، چرخه‌ی سیگنال، ایمیلِ تگ، همین عکسِ سلامت). هیچ‌جای دیگری دیده نمی‌شوند.',
    read: (f) => f.db.cron_failed_24h ?? null, good: 0, bad: 5,
    unknownWhy: () => 'این دیتابیس pg_cron ندارد یا تاریخچه‌ی اجراهایش خوانده نمی‌شود.',
  },
  {
    key: 'db_size', group: 'db', label: 'حجمِ دیتابیس', unit: 'bytes',
    guide: {
      meaning: 'کلِ دیتابیس چقدر جا گرفته. سبز و قرمز ندارد؛ فقط روندش مهم است.',
      example: 'مثلِ وزنِ کوله‌پشتی: سنگین شدنش با کتاب‌های تازه طبیعی است. ولی اگر در یک هفته دو برابر شد و تعدادِ رویدادها ثابت ماند، یعنی چیزی بی‌خود دارد تلنبار می‌شود و دیر یا زود از سقفِ پلن می‌گذرد.',
      check: [
        'خطِ تاریخچه‌ی زیرِ همین عقربه: پرشِ ناگهانی از کدام روز است.',
        'رویدادهای تازه در جدولِ پایینِ صفحه: اگر هم‌زمان بالا رفته‌اند، طبیعی است.',
        'در ویرایشگرِ SQLِ سوپابیس ببین کدام جدول بزرگ شده: `select relname, pg_size_pretty(pg_total_relation_size(relid)) from pg_statio_user_tables order by pg_total_relation_size(relid) desc limit 10;`',
      ],
      fix: [
        'جدولِ لاگی که هیچ‌وقت پاک نمی‌شود را کوتاه کن یا برایش پاک‌سازیِ خودکار بگذار.',
        'لاگِ فعالیتِ خودت را `npm run log:clear` خالی می‌کند.',
      ],
      samples: 'ده جدولِ بزرگ همین حالا — همان کوئریِ بالا، از پیش اجراشده.',
    },
    help: 'اندازه‌ی کلِ دیتابیس. حکم ندارد — فقط روندش مهم است: پرشی که با شمارِ رویدادها نمی‌خواند یعنی جدولی دارد باد می‌کند.',
    read: (f) => f.db.size_bytes, good: null, bad: null,
  },

  // ─── کرالر ───
  {
    key: 'run_age', group: 'crawler', label: 'ساعت از آخرین اجرای کامل', unit: 'hours',
    guide: {
      meaning: 'چند ساعت از آخرین باری که کرالر همه‌ی کارِ شبانه‌اش را کامل کرده گذشته.',
      example: 'کرالر مثلِ روزنامه‌فروشی است که هر شب روزنامه‌ی تازه می‌آورد. اگر سه روز نیامده، کاربران رویدادهای سه روز پیش را می‌بینند و رویدادِ این آخرِ هفته اصلاً در فهرست نیست — و اپ هیچ خطایی هم نمی‌دهد.',
      check: [
        'جدولِ «چند واقعیتِ بی‌حکم»: آخرین اجرا کی شروع شد، موفق بود یا نه، و کامل بود یا نیمه.',
        'سربرگِ «خطِ لوله‌ی کرالر»: اجرا کجا ایستاده.',
        'ماشینی که کرالر روی آن می‌چرخد روشن است و زمان‌بندِ شبانه‌اش فعال است؟',
        'اگر عقربه «نامعلوم» است و می‌گوید «ثبتِ اجرا نمی‌رسد، ولی داده می‌رسد»: رویدادهای تازه هنوز ساخته می‌شوند، پس کرالر می‌چرخد و فقط گزارشِ اجرایش (`crawler.pipeline_runs`) به این دیتابیس نمی‌رسد. نمونه‌های پایین هر دو را کنارِ هم نشان می‌دهند.',
      ],
      fix: [
        'زنجیره‌ی شبانه را در مخزنِ کرالر دستی اجرا کن و خطایش را بخوان.',
        'اگر ماشین خاموش بوده، روشنش کن؛ اجرای امشب خودش همه چیز را به‌روز می‌کند.',
        'اگر فقط ثبتِ اجرا نمی‌رسد، در مخزنِ کرالر ببین قدمِ `cloud-pull` (یا هر قدمی که اجرا را به ابر می‌فرستد) جدولِ `pipeline_runs` را هم می‌فرستد یا نه. تا آن درست نشود، عقربه‌های «فازِ شکست‌خورده» و «خطای گرفتنِ صفحه» هم به اجرای کهنه نگاه می‌کنند.',
      ],
      links: [TAB.crawler],
      samples: 'دو شاهد کنارِ هم: آخرین رویدادهای ساخته‌شده در ۷ روزِ گذشته، و آخرین اجراهایی که کرالر ثبت کرده. اگر اولی تازه است و دومی کهنه، فقط ثبتِ اجرا نمی‌رسد.',
    },
    help: 'از شروعِ آخرین اجرای کاملِ زنجیره‌ی شبانه (تعریفِ «کامل» در crawler_last_run، مایگریشن ۰۱۰۹۵). زنجیره هر شب می‌دود؛ بیش از سه روز یعنی نمی‌دود — مگر رویدادِ تازه در ۴۸ ساعتِ گذشته رسیده باشد، که آن‌وقت «نامعلوم» است (WHOSUP-289).',
    read: (f, at) => {
      if (!f.crawler.present) return null;
      if (runLogStale(f) && dataFresh(f, at)) return null;
      return f.crawler.hours_since_run ?? null;
    },
    good: 26, bad: RUN_LOG_STALE_HOURS,
    unknownWhy: (f, at) => {
      if (!f.crawler.present) return NO_CRAWLER_WHY;
      if (runLogStale(f) && dataFresh(f, at)) return RUN_LOG_MISSING_WHY;
      return 'هیچ اجرایی در pipeline_runs نیست.';
    },
  },
  {
    key: 'stage_failures', group: 'crawler', label: 'فازِ شکست‌خورده · آخرین اجرا', unit: 'count',
    guide: {
      meaning: 'در آخرین اجرای کرالر، چند قدم روی چند منبع شکست خورده.',
      example: 'کرالر هر شب برای هر سایت چند قدم برمی‌دارد: صفحه را بگیر، رویدادها را پیدا کن، تاریخ و آدرس را بخوان. اگر سایتِ تئاترِ شهر ظاهرش را عوض کند، قدمِ «رویدادها را پیدا کن» برایش شکست می‌خورد و نمایش‌هایش بی‌صدا از اپ جا می‌مانند.',
      check: [
        'سربرگِ «خطِ لوله‌ی کرالر»: کدام قدم روی کدام منبع شکست خورده.',
        'همان منبع را در سربرگِ «منابع» باز کن و «تست» بزن تا ببینی کدام قدم قرمز است.',
      ],
      fix: [
        'اگر سایتِ منبع ظاهرش را عوض کرده: استخراج‌کننده‌ی آن منبع در مخزنِ کرالر باید به‌روز شود.',
        'اگر منبع برای همیشه از بین رفته، آن را در کرالر غیرفعال کن تا هر شب دوباره شکست نخورد.',
      ],
      links: [TAB.crawler, TAB.sources],
      samples: 'قدم‌های شکست‌خورده‌ی آخرین اجرای کامل: منبع، نامِ قدم و پیامِ خطا.',
    },
    help: 'ردیف‌های crawler.runs با ok=false در همان اجرا. هر فاز روی هر منبع یک ردیف است.',
    read: (f) => (f.crawler.present ? (f.crawler.stage_failures ?? null) : null), good: 0, bad: 5,
    unknownWhy: () => NO_CRAWLER_WHY,
  },
  {
    key: 'fetch_failed', group: 'crawler', label: 'خطای گرفتنِ صفحه · آخرین اجرا', unit: 'pct',
    guide: {
      meaning: 'از همه‌ی صفحه‌هایی که کرالر در آخرین اجرا خواست بگیرد، چند درصد نیامد یا سایت جلویش را گرفت.',
      example: 'کرالر ۱۰۰ صفحه خواست و ۱۹تا یا جواب ندادند یا گفتند «تو ربات هستی، برو». رویدادهای آن ۱۹ صفحه امشب به‌روز نمی‌شوند؛ و اگر یک سایتِ بزرگ ما را کامل بسته باشد، همه‌ی رویدادهایش کم‌کم کهنه می‌شوند.',
      check: [
        'سربرگِ «خطِ لوله‌ی کرالر»: خطاها به تفکیکِ منبع — معمولاً چند سایت بیشترِ عدد را می‌سازند.',
        '«تست»ِ همان منبع در سربرگِ «منابع»: قدمِ اول (گرفتنِ صفحه) چه می‌گوید. «بسته‌شده» یعنی سایت ما را نمی‌خواهد؛ «خطا» یعنی سایت یا شبکه خراب بوده.',
      ],
      fix: [
        'خطای گذرا (سایتِ پایین) معمولاً فردا شب خودش درست می‌شود؛ یک روزِ قرمز نگران‌کننده نیست، روندِ چندروزه هست — خطِ تاریخچه را ببین.',
        'اگر سایتی ما را بسته، در مخزنِ کرالر سرعتِ درخواست به آن را کم کن یا روشِ گرفتنِ صفحه‌اش را عوض کن.',
      ],
      links: [TAB.crawler, TAB.sources],
      samples: 'منبع‌هایی که در آخرین اجرا بیشترین صفحه‌ی ناموفق را داشتند، با نوعِ خطا و یک آدرسِ نمونه.',
    },
    help: 'سهمِ درخواست‌های error یا blocked از همه‌ی درخواست‌های fetch در پنجره‌ی آخرین اجرای کامل. سایتی که ما را بلاک کرده این‌جا دیده می‌شود.',
    read: (f) => (f.crawler.present
      ? pct(f.crawler.fetch_failed_run ?? 0, f.crawler.fetch_requests_run ?? 0)
      : null),
    good: 5, bad: 35,
    unknownWhy: (f) => (f.crawler.present ? 'در آخرین اجرا هیچ درخواستی ثبت نشده.' : NO_CRAWLER_WHY),
  },
  {
    key: 'jobs_stale', group: 'crawler', label: 'کارِ مانده در صفِ ادمین', unit: 'count',
    guide: {
      meaning: 'کارهایی که از سربرگِ «منابع» خواسته‌ای (مثلاً «تست»ِ یک منبع) و بیش از یک روز است کسی برشان نداشته.',
      example: 'دکمه‌ی «تست» را می‌زنی و می‌گوید «در صف». یک روز بعد هنوز در صف است. یعنی آن طرف کسی گوش نمی‌دهد — برنامه‌ای که باید این کارها را بردارد روشن نیست.',
      check: [
        'در مخزنِ کرالر، `crawl jobs --watch` در حالِ اجراست؟',
        'به همان دیتابیسی وصل است که این ادمین به آن وصل است؟ ادمینِ ابری و ادمینِ لوکال هر کدام صفِ خودشان را دارند.',
        'جدولِ «چند واقعیتِ بی‌حکم»: چند کار در صف است و چندتا در هفت روز شکست خورده.',
      ],
      fix: [
        '`crawl jobs --watch` را در مخزنِ کرالر راه بینداز؛ صف خودش خالی می‌شود.',
        'اگر عجله نداری، اجرای شبانه هم کارهای صف را برمی‌دارد.',
      ],
      links: [TAB.sources],
      samples: 'قدیمی‌ترین کارهای مانده: نوعِ کار، هدفش و زمانِ درخواست.',
    },
    help: 'کارهای crawler_jobs که بیش از ۲۴ ساعت queued مانده‌اند. یعنی «crawl jobs --watch» نمی‌چرخد و دکمه‌های سربرگِ «منابع» به جایی نمی‌رسند.',
    read: (f) => f.crawler.jobs_stale, good: 0, bad: 3,
  },
];

/**
 * مرزهای حکم بر حسبِ خودِ عدد — «سالم تا ۹.۶٪» — تا پنجره‌ی راهنما به جای
 * «امتیاز ۶۸» بگوید چه عددی سبز است.
 *
 * از همان `LEVEL_BANDS` و همان خطِ `scoreOf` برعکس حساب می‌شود، پس با
 * عوض شدنِ یک آستانه خودش عوض می‌شود. برای شمارش‌ها رو به پایین گرد می‌شود:
 * «کارِ مانده» با آستانه‌ی ۰ و ۳ مرزِ سالمش ۰.۹ است، و «سالم تا ۱» دروغ بود
 * چون یک کارِ مانده واقعاً «مشکوک» است.
 */
export interface VerdictBounds {
  lowerIsBetter: boolean;
  /** مرزِ سالم: تا این عدد (یا از این عدد به بالا وقتی بیشتر بهتر است). */
  ok: number;
  /** مرزِ مشکوک؛ آن سوترش خراب است. */
  warn: number;
}

export function verdictBounds(good: number, bad: number, unit: Unit): VerdictBounds | null {
  if (good === bad) return null;
  const lowerIsBetter = good < bad;
  const at = (score: number) => bad + (score / 100) * (good - bad);
  const round = (v: number) => {
    if (unit === 'count') return lowerIsBetter ? Math.floor(v + 1e-9) : Math.ceil(v - 1e-9);
    return Math.round(v * 10) / 10;
  };
  return { lowerIsBetter, ok: round(at(LEVEL_BANDS.ok)), warn: round(at(LEVEL_BANDS.warn)) };
}

/** همان مرزها به زبانِ آدم، برای پنجره‌ی راهنما. */
export function describeBounds(b: VerdictBounds, unit: Unit): string {
  const v = (x: number) => formatValue(x, unit);
  return b.lowerIsBetter
    ? `سالم: تا ${v(b.ok)} · مشکوک: تا ${v(b.warn)} · خراب: بیشتر از ${v(b.warn)}`
    : `سالم: ${v(b.ok)} یا بیشتر · مشکوک: ${v(b.warn)} یا بیشتر · خراب: کمتر از ${v(b.warn)}`;
}

export interface Trend {
  prev: number | null;
  prevDay: string | null;
  /** مقدارِ امروز منهای قبلی؛ برای درصدها یعنی واحدِ درصد. */
  delta: number | null;
  /** `null` یعنی یا فرقی نکرده، یا حکمی نیست که بگوید کدام سمت بهتر است. */
  better: boolean | null;
}

export interface Gauge {
  key: string;
  group: GroupKey;
  label: string;
  help: string;
  guide: GaugeGuide;
  unit: Unit;
  value: number | null;
  /** مرزِ سالم/مشکوک/خراب بر حسبِ خودِ عدد، برای پنجره‌ی راهنما. */
  bounds: VerdictBounds | null;
  /** ۰..۱۰۰؛ `null` یعنی نامعلوم یا بی‌حکم. */
  score: number | null;
  level: Level;
  /** آستانه دارد؟ بی آن، عقربه‌ای نیست — فقط عدد و روند. */
  scored: boolean;
  /** کمتر بهتر است؟ برای رنگِ پیکانِ روند. */
  lowerIsBetter: boolean | null;
  trend: Trend;
  /** یک نقطه به‌ازای هر عکسِ روزانه، از قدیم به جدید، شاملِ امروز. */
  series: { day: string; value: number | null }[];
  unknownWhy: string | null;
  note: string | null;
  /** فقط برای عقربه‌های دارای `perCity` (WHOSUP-303). */
  cities?: CityBreakdown;
  /** در حکمِ حوزه و کل حساب نمی‌شود (`GaugeSpec.advisory`). */
  advisory: boolean;
}

/** یک شهرِ واجدِ شرط با حکمش، برای فهرستِ «بیشتر». */
export interface CityVerdict {
  city: string;
  country_code: string;
  /** اندازه‌ی شهر برای فهرست: رویدادِ زنده، یا کاربرِ فعال برای «بارِ شهر» (`sizeUnit`). */
  size: number;
  value: number | null;
  score: number | null;
  level: Level;
}

export interface CityBreakdown {
  /** عقربه‌ی «بدترین شهر»؛ `value` تهی یعنی نامعلوم (پیش از 0161 یا بی شهرِ واجد). */
  worst: {
    city: string | null;
    value: number | null;
    score: number | null;
    level: Level;
    trend: Trend;
    /** بدترین مقدارِ هر روز — ممکن است هر روز شهرِ دیگری باشد. روزِ بی `by_city` تهی است. */
    series: { day: string; value: number | null }[];
    unknownWhy: string | null;
  };
  /** «۷ از ۹ شهر سالم‌اند». */
  healthy: number;
  total: number;
  /** همه‌ی شهرهای واجد، از بد به خوب. */
  cities: CityVerdict[];
  /** واحدِ `size` در فهرست. */
  sizeUnit: 'رویداد' | 'کاربر';
}

function cityRowsOf(spec: GaugeSpec, f: HealthFacts): CityRow[] | null {
  if (spec.cityRows) return spec.cityRows(f);
  const counted = countedCities(f);
  if (!counted || !spec.perCity) return null;
  return counted.map((c) => ({ city: c.city, country_code: c.country_code, size: c.live, value: spec.perCity!(c) }));
}

/** شهرهای واجد با حکمشان، از بد به خوب؛ `null` یعنی `by_city` نیست. */
export function rankCities(spec: GaugeSpec, f: HealthFacts): CityVerdict[] | null {
  const rowsIn = cityRowsOf(spec, f);
  if (!rowsIn || spec.good == null || spec.bad == null) return null;
  const lower = spec.good < spec.bad;
  const rows = rowsIn.map((c) => {
    const score = scoreOf(c.value, spec.good!, spec.bad!);
    return { ...c, score, level: levelOf(score) };
  });
  // امتیاز اول؛ امتیازِ برابر (هر دو صفر یا صد) با خودِ عدد، بعد شهرِ بزرگ‌تر اول.
  // (∞ − ∞ عدد نیست و NaN مثلِ صفر به قدمِ بعد می‌رود.)
  return rows.sort((a, b) =>
    (a.score ?? 101) - (b.score ?? 101)
    || ((b.value ?? -Infinity) - (a.value ?? -Infinity)) * (lower ? 1 : -1)
    || b.size - a.size
    || a.city.localeCompare(b.city));
}

const worstValue = (spec: GaugeSpec, f: HealthFacts): number | null =>
  rankCities(spec, f)?.find((c) => c.value != null)?.value ?? null;

function buildCities(spec: GaugeSpec, data: HealthData, prevSnap: HealthSnapshot | null): CityBreakdown {
  const ranked = rankCities(spec, data.now) ?? [];
  const top = ranked.find((c) => c.value != null) ?? null;
  const value = top?.value ?? null;
  const score = top?.score ?? null;
  const lower = spec.good! < spec.bad!;
  const prev = prevSnap ? worstValue(spec, prevSnap.facts) : null;
  const delta = value != null && prev != null && Number.isFinite(value) && Number.isFinite(prev)
    ? Math.round((value - prev) * 10) / 10 : null;
  const better = delta == null || delta === 0 ? null : (lower ? delta < 0 : delta > 0);
  let unknownWhy: string | null = null;
  if (!top) {
    if (spec.cityRows) unknownWhy = 'هیچ شهری کاربرِ فعالِ ۶۰ روزِ گذشته ندارد.';
    else if (!Array.isArray(data.now.content.by_city)) unknownWhy = 'شمارشِ شهرها (مایگریشنِ 0161) روی این دیتابیس اجرا نشده.';
    else if (ranked.length) unknownWhy = 'این عدد به تفکیکِ شهر هنوز شمرده نمی‌شود — نسخه‌ی تازه‌ی مایگریشنِ 0161 روی این دیتابیس اجرا نشده.';
    else unknownWhy = `هیچ شهری ${CITY_MIN_LIVE} رویدادِ زنده ندارد و اتاوا هم رویدادِ زنده ندارد.`;
  }
  return {
    worst: {
      city: top?.city ?? null, value, score, level: levelOf(score),
      trend: { prev, prevDay: prevSnap?.day ?? null, delta, better },
      series: data.history.map((s) => ({ day: s.day, value: worstValue(spec, s.facts) })),
      unknownWhy,
    },
    healthy: ranked.filter((c) => c.level === 'ok').length,
    total: ranked.length,
    cities: ranked,
    sizeUnit: spec.cityRows ? 'کاربر' : 'رویداد',
  };
}

/**
 * امتیازی که عقربه در میانگینِ حوزه دارد: بدترینِ «کل» و «بدترین شهر»
 * (تصمیمِ WHOSUP-303) — یک شهرِ خراب نباید در میانگینِ همه گم شود.
 */
export function effectiveScore(g: Gauge): number | null {
  const own = [g.score, g.cities?.worst.score].filter((s): s is number => s != null);
  return own.length ? Math.min(...own) : null;
}

export interface GroupReport {
  key: GroupKey;
  label: string;
  help: string;
  score: number | null;
  level: Level;
  gauges: Gauge[];
}

export interface HealthReport {
  asOf: string;
  overall: { score: number | null; level: Level };
  groups: GroupReport[];
  /** روزِ مقایسه — `null` یعنی تاریخچه فقط امروز را دارد. */
  comparedTo: string | null;
  /** چند عکسِ روزانه در تاریخچه (با امروز). */
  snapshots: number;
}

const mean = (xs: (number | null)[]): number | null => {
  const v = xs.filter((x): x is number => x != null);
  return v.length ? Math.round(v.reduce((a, b) => a + b, 0) / v.length) : null;
};

const dayOf = (iso: string): string => iso.slice(0, 10);

/** آخرین عکسِ روزی غیر از امروز — مبنای «نسبت به قبل». */
export function previousSnapshot(history: HealthSnapshot[], today: string): HealthSnapshot | null {
  for (let i = history.length - 1; i >= 0; i--) {
    if (history[i].day < today) return history[i];
  }
  return null;
}

export function buildGauge(spec: GaugeSpec, data: HealthData): Gauge {
  const at = Date.parse(data.as_of);
  const value = spec.read(data.now, at);
  const scored = spec.good != null && spec.bad != null;
  const score = scored ? scoreOf(value, spec.good!, spec.bad!) : null;
  const lowerIsBetter = scored ? spec.good! < spec.bad! : null;

  const today = dayOf(data.as_of);
  const prevSnap = previousSnapshot(data.history, today);
  const prev = prevSnap ? spec.read(prevSnap.facts, Date.parse(prevSnap.taken_at)) : null;
  // ∞ − ∞ عدد نیست، و ∞ − ۵ هم «تغییر» نیست که بشود نوشت.
  const delta = value != null && prev != null && Number.isFinite(value) && Number.isFinite(prev)
    ? Math.round((value - prev) * 10) / 10 : null;
  let better: boolean | null = null;
  if (delta != null && delta !== 0 && lowerIsBetter != null) {
    better = lowerIsBetter ? delta < 0 : delta > 0;
  }

  return {
    key: spec.key, group: spec.group, label: spec.label, help: spec.help, guide: spec.guide,
    unit: spec.unit, bounds: scored ? verdictBounds(spec.good!, spec.bad!, spec.unit) : null,
    value, score, level: scored ? levelOf(score) : 'unknown', scored, lowerIsBetter,
    trend: { prev, prevDay: prevSnap?.day ?? null, delta, better },
    series: data.history.map((s) => ({
      day: s.day, value: spec.read(s.facts, Date.parse(s.taken_at)),
    })),
    unknownWhy: value == null ? (spec.unknownWhy?.(data.now, at) ?? 'داده‌ای برای این عقربه نیست.') : null,
    note: value != null ? (spec.note?.(data.now) ?? null) : null,
    advisory: spec.advisory === true,
    ...((spec.perCity || spec.cityRows) && scored ? { cities: buildCities(spec, data, prevSnap) } : {}),
  };
}

export function buildHealthReport(data: HealthData, specs: readonly GaugeSpec[] = GAUGES): HealthReport {
  const gauges = specs.map((s) => buildGauge(s, data));
  const groups = (Object.keys(GROUP_LABEL) as GroupKey[]).map((key) => {
    const own = gauges.filter((g) => g.group === key);
    const score = mean(own.filter((g) => g.scored && !g.advisory).map(effectiveScore));
    return { key, label: GROUP_LABEL[key], help: GROUP_HELP[key], score, level: levelOf(score), gauges: own };
  });
  // ⚠ کل = **ضعیف‌ترین حوزه**، نه میانگین: با میانگین، سه حوزه‌ی سالم یک
  // حوزه‌ی خراب را پنهان می‌کردند (۱۰۰+۱۰۰+۱۰۰+۰ = ۷۵ = سبز). سیستم همان‌قدر
  // سالم است که بیمارترین تکه‌اش. چهار عقربه‌ی کنارش می‌گویند کدام تکه.
  const groupScores = groups.map((g) => g.score).filter((s): s is number => s != null);
  const overall = groupScores.length ? Math.min(...groupScores) : null;
  const today = dayOf(data.as_of);
  return {
    asOf: data.as_of,
    overall: { score: overall, level: levelOf(overall) },
    groups,
    comparedTo: previousSnapshot(data.history, today)?.day ?? null,
    snapshots: data.history.length,
  };
}

/** عدد با واحدش، برای زیرِ عقربه. `null` → «—». */
export function formatValue(value: number | null, unit: Unit): string {
  if (value === Infinity) return '∞';
  if (value == null || !Number.isFinite(value)) return '—';
  switch (unit) {
    case 'pct': return `${Number.isInteger(value) ? value : value.toFixed(1)}٪`;
    case 'hours': return value >= 48
      ? `${Math.round(value / 24)} روز`
      : `${Number.isInteger(value) ? value : value.toFixed(1)} ساعت`;
    case 'ratio': return `${Number.isInteger(value) ? value : value.toFixed(1)}`;
    case 'ms': return `${Math.round(value).toLocaleString('en-US')} ms`;
    case 'bytes': return value >= 1e9
      ? `${(value / 1e9).toFixed(1)} GB`
      : `${Math.round(value / 1e6).toLocaleString('en-US')} MB`;
    default: return Math.round(value).toLocaleString('en-US');
  }
}

/** تغییر با علامت، به همان واحد. */
export function formatDelta(delta: number | null, unit: Unit): string {
  if (delta == null) return '';
  if (delta === 0) return 'بی‌تغییر';
  const sign = delta > 0 ? '+' : '−';
  return `${sign}${formatValue(Math.abs(delta), unit)}`;
}
